import { ContextualTipId } from '@resistapp/client/contexts/contextual-tip-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { GeneAndCopyNumber } from '@resistapp/client/data-utils/plot-data/process-overview-line-datum';
import { getMetricColor } from '@resistapp/client/utils/metric-utils';
import { OverviewChartConfiguration } from '@resistapp/client/utils/overview-chart-configurations';
import type { L2Targets } from '@resistapp/common/assays';
import { MetricMode } from '@resistapp/common/types';
import { Bar } from '@visx/shape';
import { Text } from '@visx/text';
import { ScaleBand, ScaleLinear } from 'd3-scale';
import { isNil, min } from 'lodash';
import { Fragment, MouseEventHandler } from 'react';
import { theme } from '../shared/theme';
import { BarLabelThreshold, getScaleLabel, tickLabelProps } from './copy-number-bar-graph';

const textMargin = 6 as const;
const maxBarHeight = 42 as const;

interface BarProps {
  genesAndNumbers: GeneAndCopyNumber[];
  geneAndCopyNumber: GeneAndCopyNumber;
  metricMode: MetricMode;
  scaleProperty: keyof GeneAndCopyNumber;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleBand<string>;
  handleMouseMove?: (
    event: React.MouseEvent | React.TouchEvent,
    newTooltipData: GeneAndCopyNumber | undefined,
    enter?: boolean,
  ) => void;
  activeOverviewConfiguration: OverviewChartConfiguration;
  markInteraction?: (tipId: ContextualTipId) => void;
  setHoveredTarget?: (target: GeneAndCopyNumber | undefined | null) => void;
}

export function CopyNumberBar(props: BarProps) {
  const {
    genesAndNumbers,
    geneAndCopyNumber,
    metricMode,
    scaleProperty,
    xScale,
    yScale,
    handleMouseMove,
    activeOverviewConfiguration,
    markInteraction,
    setHoveredTarget,
  } = props;
  const { queryFilters } = useSampleDataContext();
  const { activeAbundanceMode } = useOverviewContext();
  const { gene, assay } = geneAndCopyNumber;
  const scaleValue = geneAndCopyNumber[scaleProperty] as number;
  const originalBarHeight = yScale.bandwidth();
  const barHeight = min([originalBarHeight, maxBarHeight]) || 0;
  const barHeightDiff = originalBarHeight - barHeight;
  if (isNil(scaleValue)) {
    return null;
  }
  const formattedNumber = activeOverviewConfiguration.formatNumber?.(scaleValue);
  const barWidth = xScale(scaleValue);
  const barY = (yScale(getScaleLabel(assay, gene)) || 0) + barHeightDiff / 2;
  const barX = 0;
  const textPosition = getTextPosition(barWidth);

  // Determine the bar color based on the metric mode
  const barColor =
    metricMode === MetricMode.REDUCTION
      ? getMetricColor(scaleValue, MetricMode.REDUCTION, activeAbundanceMode)
      : theme.colors.neutral700;

  const onMouseProps: Record<string, MouseEventHandler<SVGRectElement>> = handleMouseMove
    ? {
        onMouseLeave: event => {
          handleMouseMove(event, undefined);
          setHoveredTarget?.(null);
        },
        onMouseMove: event => {
          const geneData = genesAndNumbers.find(d => d.assay === assay);
          handleMouseMove(event, geneData, true);
        },
        onMouseEnter: event => {
          const geneData = genesAndNumbers.find(d => d.assay === assay);
          handleMouseMove(event, geneData, true);
          setHoveredTarget?.(geneData);
        },
      }
    : {};

  const isTextOnBar = !textPosition.isOutside;

  // Determine the text color
  const textColor =
    metricMode === MetricMode.REDUCTION && isTextOnBar
      ? activeOverviewConfiguration.getFriendlyTextColor(scaleValue)
      : textPosition.isOutside
        ? tickLabelProps.color
        : 'white';

  const opacity =
    queryFilters.filters.selectedTargets.length === 16 || // Hack: default all targets selected
    queryFilters.filters.selectedTargets.includes(assay as L2Targets)
      ? 1
      : 0.5;

  const onClick = () => {
    queryFilters.toggleSingleTarget(assay as L2Targets);
    markInteraction?.(ContextualTipId.SITE_FOCUS_GENE_OR_ANTIBIOTIC);
  };

  return (
    <Fragment key={`bar-${gene}`}>
      <Bar
        key={`bar-${gene}`}
        y={barY}
        x={barX}
        rx={3}
        width={barWidth}
        height={barHeight}
        fill={barColor}
        cursor="pointer"
        onClick={onClick}
        opacity={opacity}
        {...onMouseProps}
      />
      <Text
        x={textPosition.x}
        y={barY + barHeight / 2}
        textAnchor={textPosition.textAnchor}
        dy=".35em"
        fill={textColor}
        style={tickLabelProps}
        cursor="pointer"
        onClick={onClick}
        {...onMouseProps}
      >
        {formattedNumber}
      </Text>
    </Fragment>
  );
}

type LabelPosition = 'start' | 'end';
function getTextPosition(barWidth: number) {
  const isOutside = barWidth < BarLabelThreshold;
  return {
    x: barWidth + (isOutside ? textMargin : -textMargin),
    isOutside,
    textAnchor: (isOutside ? 'start' : 'end') as LabelPosition,
  };
}

